import { apiClient } from '@/services/apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import type { FormSchema, FormSubmitValues } from '../form-builder';
import type { WorkflowStateResponse } from '../types/workflow';

interface SubmitEventResponse {
  id?: number;
  message?: string;
  // Forward-compat; resource handlers vary.
  [key: string]: unknown;
}

/**
 * Backoffice-side workflow driver.
 *
 * Originally targeted the program-level `/workflow/ONB/*` mount, but
 * that mount now returns `400 / ROUTE_DISABLED` for candidate cases
 * under multi-workflow support. Repointed to the invite-workflow
 * mount at `/console/user_onboarding/invite/workflow/*`, which
 * accepts both FusionAuth JWTs (this service's `apiClient`) and
 * invite tokens (candidate portal's `inviteApiClient`). The server
 * resolves the correct workflow from `candidate_case`, so there's no
 * `ONB` / `CR` segment in the URL — same code path drives every
 * workflow.
 *
 * Method surface is unchanged so call sites (`CandidateDetailPage`,
 * `WorkflowEventModal`) don't need to know the URL moved.
 */
class WorkflowService {
  private base = apiConfig.endpoints.userOnboarding.inviteWorkflow;

  /** Current state + role-filtered next events for one case. */
  async getState(entityId: number | string): Promise<WorkflowStateResponse['data']> {
    try {
      const response = await apiClient.get<WorkflowStateResponse>(
        `${this.base}/${encodeURIComponent(String(entityId))}`,
      );
      return response.data.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Workflow State');
    }
  }

  /** Form metadata for one transition (same shape as the survey-style form schema). */
  async getEventMetadata(entityId: number | string, event: string): Promise<FormSchema> {
    try {
      const response = await apiClient.get<{ data: FormSchema }>(
        `${this.base}/${encodeURIComponent(String(entityId))}/${encodeURIComponent(event)}`,
      );
      return response.data.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Workflow Event Form');
    }
  }

  /** Fire a workflow transition for `:event` with the prepared body. */
  async submitEvent(
    entityId: number | string,
    event: string,
    payload: FormSubmitValues,
  ): Promise<SubmitEventResponse> {
    try {
      const response = await apiClient.post<SubmitEventResponse>(
        `${this.base}/${encodeURIComponent(String(entityId))}/${encodeURIComponent(event)}`,
        payload,
      );
      return response.data;
    } catch (error: any) {
      // Surface server-supplied `message` / `error` to the caller — the
      // workflow handler returns user-safe copy (e.g. "field is required").
      const data = error?.response?.data;
      if (data && (data.message || data.error)) {
        const wrapped = new Error(data.message || data.error);
        (wrapped as any).serverError = data;
        throw wrapped;
      }
      handleServiceError(error, 'Submit Workflow Transition');
    }
  }
}

export const workflowService = new WorkflowService();
export default workflowService;
